import { useCallback, useEffect } from 'react'

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'
import { FILE_STALE_TIME } from '@/constants/file'

import { buildPaginatedEndpoint } from '@/helpers/api'

import { useLatestRef } from '@/hooks/common/useLatestRef'
import { organizationFilesQueryKeys } from '@/hooks/organization/types'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

interface UseOrganizationFilesArgs<T> {
  orgId: UUID | ''
  enabled?: boolean
  staleTime?: number
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
  selector?: (data: PaginatedResponse<UnhydratedFile>) => T
}

export const fetchOrganizationFiles = async (
  orgId: string | undefined,
  accessToken: string | undefined | null,
  pagination: PaginateRequest = { limit: 10000 },
): Promise<PaginatedResponse<UnhydratedFile>> => {
  if (accessToken === null || accessToken === undefined || !orgId) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/files`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as PaginatedResponse<UnhydratedFile>
}

interface UseOrganizationFilesQueryOptionsArgs {
  orgId: UUID | ''
  enabled?: boolean
}

export const useOrganizationFilesQueryOptions = ({
  orgId,
  enabled = true,
}: UseOrganizationFilesQueryOptionsArgs) => {
  const { accessToken } = useAuthInfo()

  return queryOptions({
    queryKey: organizationFilesQueryKeys.organization({ orgId }),
    queryFn: () => fetchOrganizationFiles(orgId, accessToken, { limit: 10000 }),
    staleTime: FILE_STALE_TIME,
    enabled: enabled && !!orgId,
  })
}

export const useOrganizationFiles = <T = UnhydratedFile[]>({
  orgId,
  enabled = true,
  staleTime = FILE_STALE_TIME,
  refetchOnMount = true,
  refetchOnWindowFocus = true,
  selector,
}: UseOrganizationFilesArgs<T>) => {
  const options = useOrganizationFilesQueryOptions({ orgId, enabled })

  const select = useCallback(
    (data: PaginatedResponse<UnhydratedFile>) => {
      if (selector) {
        return selector(data)
      }
      return data.data.sort((a, b) =>
        a.name.localeCompare(b.name),
      ) as unknown as T
    },
    [selector],
  )

  return useQuery({
    ...options,
    staleTime,
    refetchOnMount,
    refetchOnWindowFocus,
    select,
  })
}

interface UseOrganizationFilesPrefetchArgs {
  orgId: UUID | ''
}

export const useOrganizationFilesPrefetch = ({
  orgId,
}: UseOrganizationFilesPrefetchArgs) => {
  const queryClient = useQueryClient()
  const optionsRef = useLatestRef(useOrganizationFilesQueryOptions({ orgId }))

  useEffect(() => {
    queryClient.prefetchQuery(optionsRef.current)
  }, [optionsRef, queryClient])
}

interface UseOrganizationFilesCountArgs {
  orgId: UUID | ''
}

interface OrganizationFilesCount {
  count: number
}

const selectFileCount = (
  data: PaginatedResponse<UnhydratedFile>,
): OrganizationFilesCount => {
  return { count: data.count }
}

export const useOrganizationFilesCount = ({
  orgId,
}: UseOrganizationFilesCountArgs) => {
  return useOrganizationFiles<OrganizationFilesCount>({
    orgId,
    selector: selectFileCount,
  })
}
